import React, { useState, useEffect, useRef } from 'react';
import { View, Text, Pressable, StyleSheet, Modal, TouchableWithoutFeedback, TextInput, ScrollView, ActivityIndicator } from 'react-native';
import { 
  ChevronDown, 
  ChevronUp, 
  CheckCircle2, 
  X, 
  AlertCircle,
  Play,
  Pause,
  RotateCcw
} from '../lib/icons';
import { shadows } from '../lib/styles';
import { ClarifyQuestionsForm } from './ClarifyQuestionsForm';
import { PlanOverrideForm, PlanOverride as PlanOverrideType } from './PlanOverrideForm';
import { ProviderSelectionList } from './ProviderSelectionList';
import { OutreachProviderList, ProviderStatus, OutreachOwner, ContactAttempt } from './OutreachProviderList';
import { HoneyDonePlanWidget } from './HoneyDonePlanWidget';
import { AnalyzeRecommendationsPanel } from './AnalyzeRecommendationsPanel';

export type { PlanOverrideType as PlanOverride };

export type StageStatus = 'pending' | 'in_progress' | 'completed' | 'blocked' | 'awaiting_confirmation';
export type StageName = 'triage' | 'clarify' | 'plan' | 'outreach' | 'analyze' | 'schedule';

export type SubstepStatus = 'pending' | 'in_progress' | 'completed';

export interface StageSubstep {
  id: string;
  title: string;
  completed: boolean;
}

export interface ClarifyQuestion {
  id: string;
  question: string;
  answer: string;
}

export interface StageData {
  status: StageStatus;
  started_at: string | null;
  completed_at: string | null;
  human_intervention: boolean;
  notes: string;
  substeps: StageSubstep[];
  awaiting_confirmation: boolean;
  clarifying_questions?: ClarifyQuestion[];
}

interface AIAgentStageProps {
  stageName: StageName;
  stageData: StageData;
  stageNumber: number;
  stageTitle: string;
  stagePurpose: string;
  isActive: boolean;
  icon: React.ReactNode;
  onConfirm: () => void;
  onReset: () => void;
  onIntervene: () => void;
  onResumeAI: () => void;
  onStart: () => void;
  onSubmitClarifyAnswers?: (answers: ClarifyQuestion[]) => Promise<void>;
  onPlanOverride?: (override: PlanOverrideType, reason: string) => Promise<void>;
  onAnalyzeOverride?: (options: any[], recommendedOptionId: string, reason: string) => Promise<void>;
  onProvidersChange?: (providers: any[]) => void;
  onProviderStatusChange?: (providerId: string, status: ProviderStatus) => void;
  onContactProgressChange?: (providerId: string, contactProgress: ContactAttempt[]) => void;
  onContactMethodChange?: (providerId: string, method: string) => void;
  onOutreachOwnerChange?: (providerId: string, owner: OutreachOwner) => void;
  onRunProviderOutreach?: (providerId: string) => void;
  onCompleteManually?: () => void;
  canStart: boolean;
  disabled?: boolean;
}

export const AIAgentStage: React.FC<AIAgentStageProps> = ({
  stageName,
  stageData,
  stageNumber,
  stageTitle,
  stagePurpose,
  isActive,
  icon,
  onConfirm,
  onReset,
  onIntervene,
  onResumeAI,
  onStart,
  onSubmitClarifyAnswers,
  onPlanOverride,
  onAnalyzeOverride,
  onProvidersChange,
  onProviderStatusChange,
  onContactProgressChange,
  onContactMethodChange,
  onOutreachOwnerChange,
  onRunProviderOutreach,
  onCompleteManually,
  canStart,
  disabled = false
}) => {
  const [isExpanded, setIsExpanded] = useState(isActive);
  const [showInterventionModal, setShowInterventionModal] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [interventionNotes, setInterventionNotes] = useState('');
  const [isNotesExpanded, setIsNotesExpanded] = useState(false);
  
  // Track substep progress states
  const [substepStates, setSubstepStates] = useState<Map<string, SubstepStatus>>(new Map());
  const substepTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Initialize substep states and auto-advance when stage is in progress
  useEffect(() => {
    if (!stageData.substeps || stageData.substeps.length === 0) return;
    
    // Clear any existing timeout
    if (substepTimeoutRef.current) {
      clearTimeout(substepTimeoutRef.current);
      substepTimeoutRef.current = null;
    }
    
    // Initialize states based on completed status from database
    const initialStates = new Map<string, SubstepStatus>();
    stageData.substeps.forEach((substep) => {
      // Always respect the database completion status
      if (substep.completed) {
        initialStates.set(substep.id, 'completed');
      } else {
        initialStates.set(substep.id, 'pending');
      }
    });
    
    // If stage is completed or awaiting confirmation, mark all substeps as completed
    // This handles cases where the database marks them complete but local state hasn't updated
    if (stageData.status === 'completed' || stageData.status === 'awaiting_confirmation') {
      stageData.substeps.forEach((substep) => {
        initialStates.set(substep.id, 'completed');
      });
      setSubstepStates(initialStates);
      return;
    }
    
    setSubstepStates(initialStates);
    
    // Auto-advance substeps if stage is in progress
    if (stageData.status === 'in_progress' && !stageData.human_intervention) {
      startSubstepAnimation(initialStates, 0);
    }
    
    return () => {
      if (substepTimeoutRef.current) {
        clearTimeout(substepTimeoutRef.current);
      }
    };
  }, [stageData.status, stageData.substeps, stageData.human_intervention]);
  
  const startSubstepAnimation = (currentStates: Map<string, SubstepStatus>, currentIndex: number) => {
    if (!stageData.substeps || currentIndex >= stageData.substeps.length) return;
    
    const currentSubstep = stageData.substeps[currentIndex];
    const currentState = currentStates.get(currentSubstep.id);
    
    // Skip if already completed
    if (currentState === 'completed') {
      startSubstepAnimation(currentStates, currentIndex + 1);
      return;
    }
    
    // Set current substep to in_progress
    const updatedStates = new Map(currentStates);
    updatedStates.set(currentSubstep.id, 'in_progress');
    setSubstepStates(updatedStates);
    
    // Special handling for Clarify stage: Stop at "Wait for homeowner responses"
    if (stageName === 'clarify' && currentSubstep.id === 'clarify-3') {
      // Keep this step in progress until answers are submitted
      // It will be advanced manually when handleSubmitClarifyAnswers is called
      return;
    }
    
    // Check if this is the last substep
    const isLastSubstep = currentIndex === stageData.substeps.length - 1;
    
    if (isLastSubstep) {
      // Keep last substep in progress until AI completes
      // It will be marked as completed when stage status changes to 'completed'
      return;
    }
    
    // After 2 seconds, mark as completed and move to next
    substepTimeoutRef.current = setTimeout(() => {
      const newStates = new Map(updatedStates);
      newStates.set(currentSubstep.id, 'completed');
      setSubstepStates(newStates);
      
      // Move to next substep
      startSubstepAnimation(newStates, currentIndex + 1);
    }, 2000);
  };
  
  // Detect when clarify answers are submitted and advance substeps
  useEffect(() => {
    if (stageName !== 'clarify' || !stageData.substeps) return;
    
    // Check if "Wait for homeowner responses" step should be completed
    // This happens when:
    // 1. The substep is marked as completed in the database, OR
    // 2. The stage status changes (indicating answers were processed)
    const waitStep = stageData.substeps.find(s => s.id === 'clarify-3');
    const validateStep = stageData.substeps.find(s => s.id === 'clarify-4');
    
    if (waitStep && validateStep) {
      const waitStepState = substepStates.get('clarify-3');
      const validateStepState = substepStates.get('clarify-4');
      
      // If wait step is completed in database but still in progress locally, advance
      if (waitStep.completed && waitStepState === 'in_progress') {
        const updatedStates = new Map(substepStates);
        updatedStates.set('clarify-3', 'completed');
        updatedStates.set('clarify-4', 'in_progress');
        setSubstepStates(updatedStates);
      }
      
      // If validation step is completed in database, mark it complete locally
      if (validateStep.completed && validateStepState !== 'completed') {
        const updatedStates = new Map(substepStates);
        updatedStates.set('clarify-4', 'completed');
        setSubstepStates(updatedStates);
      }
    }
  }, [stageData.substeps, stageName, substepStates]);

  const getStatusColor = (status: StageStatus) => {
    switch (status) {
      case 'pending':
        return { color: '#9CA3AF', backgroundColor: '#F3F4F6', borderColor: '#D1D5DB' };
      case 'in_progress':
        return { color: '#2563EB', backgroundColor: '#EFF6FF', borderColor: '#2563EB' };
      case 'completed':
        return { color: '#16A34A', backgroundColor: '#F0FDF4', borderColor: '#16A34A' };
      case 'blocked':
        return { color: '#DC2626', backgroundColor: '#FEE2E2', borderColor: '#DC2626' };
      case 'awaiting_confirmation':
        return { color: '#F59E0B', backgroundColor: '#FEF3C7', borderColor: '#F59E0B' };
      default:
        return { color: '#9CA3AF', backgroundColor: '#F3F4F6', borderColor: '#D1D5DB' };
    }
  };

  const getStatusLabel = (status: StageStatus) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'in_progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      case 'blocked':
        return 'Blocked';
      case 'awaiting_confirmation':
        return 'Awaiting Confirmation';
      default:
        return 'Unknown';
    }
  };

  const statusColors = getStatusColor(stageData.status);
  const handleInterventionSubmit = () => {
    setShowInterventionModal(false);
    setInterventionNotes('');
    onIntervene();
  };

  const handleReset = () => {
    setShowResetConfirm(false);
    onReset();
  };

  return (
    <>
      <View style={[
        styles.stageCard,
        { borderLeftColor: statusColors.borderColor, borderLeftWidth: 4 }
      ]}>
        {/* Stage Header */}
        <Pressable 
          onPress={() => setIsExpanded(!isExpanded)}
          style={styles.stageHeader}
        >
          <View style={styles.stageHeaderLeft}>
            <View style={[styles.iconContainer, { backgroundColor: statusColors.backgroundColor }]}>
              {icon}
            </View>
            <View style={styles.stageTitleContainer}>
              <View style={styles.stageTitleRow}>
                <Text style={styles.stageNumber}>Stage {stageNumber}</Text>
                <View style={[styles.statusBadge, { backgroundColor: statusColors.backgroundColor }]}>
                  <Text style={[styles.statusBadgeText, { color: statusColors.color }]}>
                    {getStatusLabel(stageData.status)}
                  </Text>
                </View>
              </View>
              <Text style={styles.stageTitle}>{stageTitle}</Text>
              {!isExpanded && (
                <Text style={styles.stagePurposeCollapsed} numberOfLines={1}>
                  {stagePurpose}
                </Text>
              )}
            </View>
          </View>
          <View style={styles.expandIcon}>
            {isExpanded ? (
              <ChevronUp size={20} color="#6B7280" />
            ) : (
              <ChevronDown size={20} color="#6B7280" />
            )}
          </View>
        </Pressable>

        {/* Stage Body - Expanded */}
        {isExpanded && (
          <View style={styles.stageBody}>
            <Text style={styles.stagePurpose}>{stagePurpose}</Text>

            {/* Human Intervention Badge */}
            {stageData.human_intervention && (
              <View style={styles.interventionBadge}>
                <AlertCircle size={16} color="#F59E0B" />
                <Text style={styles.interventionBadgeText}>Human Override Active</Text>
              </View>
            )}

            {/* Substeps */}
            {stageName !== 'outreach' && stageData.substeps && stageData.substeps.length > 0 && (
              <View style={styles.substepsContainer}>
                <Text style={styles.substepsTitle}>Progress</Text>
                {stageData.substeps.map((substep) => {
                  // Determine substep status: prioritize database completion, then local state
                  let substepStatus: SubstepStatus;
                  
                  // If database says completed, always show as completed
                  if (substep.completed) {
                    substepStatus = 'completed';
                  } else {
                    // Otherwise use local animation state
                    substepStatus = substepStates.get(substep.id) || 'pending';
                  }
                  
                  // If stage is awaiting_confirmation or completed, force all to completed
                  if ((stageData.status === 'awaiting_confirmation' || stageData.status === 'completed') && !substep.completed) {
                    substepStatus = 'completed';
                  }
                  
                  return (
                    <View key={substep.id} style={styles.substepItem}>
                      <View style={[
                        styles.substepIndicator,
                        substepStatus === 'completed' && styles.substepIndicatorCompleted,
                        substepStatus === 'in_progress' && styles.substepIndicatorInProgress
                      ]}>
                        {substepStatus === 'completed' && (
                          <CheckCircle2 size={14} color="#16A34A" />
                        )}
                        {substepStatus === 'in_progress' && (
                          <ActivityIndicator size="small" color="#2563EB" />
                        )}
                      </View>
                      <Text style={[
                        styles.substepText,
                        substepStatus === 'completed' && styles.substepTextCompleted,
                        substepStatus === 'in_progress' && styles.substepTextInProgress
                      ]}>
                        {substep.title}
                      </Text>
                    </View>
                  );
                })}
              </View>
            )}

            {/* Clarifying Questions Form (for Clarify stage) */}
            {(() => {
              // Only show form if:
              // 1. Stage is clarify
              // 2. Has unanswered questions (questions without answers)
              // 3. Stage is in progress
              // 4. Not awaiting confirmation
              // 5. Has callback function
              const unansweredQuestions = stageData.clarifying_questions?.filter(
                (q) => !q.answer || q.answer.trim().length === 0
              ) || [];
              
              const shouldShowForm = stageName === 'clarify' && 
                                     unansweredQuestions.length > 0 &&
                                     stageData.status === 'in_progress' &&
                                     !stageData.awaiting_confirmation &&
                                     onSubmitClarifyAnswers;
              
              console.log('Clarify form check:', {
                stageName,
                totalQuestions: stageData.clarifying_questions?.length || 0,
                unansweredQuestions: unansweredQuestions.length,
                status: stageData.status,
                awaitingConfirmation: stageData.awaiting_confirmation,
                hasCallback: !!onSubmitClarifyAnswers,
                shouldShowForm
              });
              
              return shouldShowForm ? (
                <View style={styles.clarifyFormContainer}>
                  <ClarifyQuestionsForm
                    questions={unansweredQuestions}
                    onSubmit={onSubmitClarifyAnswers}
                    disabled={disabled || stageData.human_intervention}
                  />
                </View>
              ) : null;
            })()}

            {/* HoneyDone Plan Widget (for Plan stage with in-house recommendation) */}
            {stageName === 'plan' &&
             (stageData.status === 'awaiting_confirmation' || stageData.status === 'completed') &&
             ((stageData as any).plan_data?.approach === 'in_house' || 
              (stageData as any).plan_data?.approach_details?.in_house_work) && (
              <View style={styles.honeyDonePlanContainer}>
                <HoneyDonePlanWidget
                  planData={(stageData as any).plan_data || {}}
                />
              </View>
            )}

            {/* Provider Selection (for Plan stage with dispatch) */}
            {stageName === 'plan' &&
             (stageData.status === 'awaiting_confirmation' || stageData.status === 'completed') &&
             (stageData as any).plan_data?.approach !== 'in_house' &&
             !(stageData as any).plan_data?.approach_details?.in_house_work &&
             (stageData as any).plan_data?.provider_recommendations?.length > 0 && (
              <View style={styles.providerSelectionContainer}>
                <ProviderSelectionList
                  initialProviders={
                    (stageData as any).plan_data?.provider_recommendations?.map((rec: any) => ({
                      id: rec.id || `mock-${rec.specialty}`,
                      name: rec.name || rec.specialty,
                      specialty: rec.specialty,
                      reason: rec.reason,
                      skipped: rec.skipped ?? false, // Preserve skipped state from database
                      suggested_contact_method: rec.suggested_contact_method,
                      contact_method: rec.contact_method,
                      contact_method_reason: rec.contact_method_reason,
                      phone: rec.phone,
                      email: rec.email,
                      website: rec.website,
                      can_contact_via_call: rec.can_contact_via_call,
                      can_contact_via_text: rec.can_contact_via_text,
                      can_contact_via_email: rec.can_contact_via_email,
                    })) || []
                  }
                  onProvidersChange={onProvidersChange || (() => {})}
                  disabled={disabled || stageData.status === 'completed'}
                />
              </View>
            )}

            {/* Plan Override Form (for Plan stage) */}
            {stageName === 'plan' &&
             stageData.status === 'awaiting_confirmation' &&
             onPlanOverride && (
              <View style={styles.overrideFormContainer}>
                <PlanOverrideForm
                  currentApproach={(stageData as any).plan_data?.approach}
                  planData={(stageData as any).plan_data}
                  onOverride={onPlanOverride}
                  disabled={disabled}
                />
              </View>
            )}

            {/* Analyze Recommendations + Human Override */}
            {stageName === 'analyze' &&
             (stageData.status === 'awaiting_confirmation' || stageData.status === 'completed') &&
             onAnalyzeOverride && (
              <View style={styles.overrideFormContainer}>
                <AnalyzeRecommendationsPanel
                  analysisData={(stageData as any).analysis_data}
                  onOverride={onAnalyzeOverride}
                  disabled={disabled || stageData.status === 'completed'}
                />
              </View>
            )}

            {/* Outreach Provider List (for Outreach stage) */}
            {stageName === 'outreach' &&
             (stageData.status === 'pending' || stageData.status === 'in_progress' || stageData.status === 'awaiting_confirmation' || stageData.status === 'completed') && (
              <View style={styles.outreachProviderContainer}>
                <OutreachProviderList
                  providers={
                    ((stageData as any).providers_to_contact || []).map((p: any) => ({
                      id: p.id,
                      name: p.name,
                      specialty: p.specialty,
                      reason: p.reason,
                      status: p.status || 'pending',
                      contact_progress: p.contact_progress || [],
                      last_contacted_at: p.last_contacted_at,
                      outreach_owner: p.outreach_owner,
                      suggested_contact_method: p.suggested_contact_method,
                      contact_method: p.contact_method,
                      contact_method_reason: p.contact_method_reason,
                    }))
                  }
                  onProviderStatusChange={onProviderStatusChange || (() => {})}
                  onContactProgressChange={onContactProgressChange}
                  onContactMethodChange={onContactMethodChange}
                  onOutreachOwnerChange={onOutreachOwnerChange}
                  onRunAgentOutreach={onRunProviderOutreach}
                  disabled={disabled || stageData.status === 'completed'}
                />
              </View>
            )}

            {/* AI Notes */}
            {stageData.notes && (
              <View style={styles.notesContainer}>
                <Pressable
                  style={styles.notesHeader}
                  onPress={() => setIsNotesExpanded(!isNotesExpanded)}
                >
                  <Text style={styles.notesTitle}>AI Analysis</Text>
                  {isNotesExpanded ? (
                    <ChevronUp size={16} color="#6B7280" />
                  ) : (
                    <ChevronDown size={16} color="#6B7280" />
                  )}
                </Pressable>
                {isNotesExpanded && (
                  <View style={styles.notesBox}>
                    <Text style={styles.notesText}>{stageData.notes}</Text>
                  </View>
                )}
              </View>
            )}

            {/* Action Buttons */}
            <View style={styles.actionsContainer}>
              {/* Start Agent Button */}
              {canStart && stageData.status === 'pending' && !stageData.human_intervention && (
                <Pressable
                  style={[styles.actionButton, styles.startButton, disabled && styles.actionButtonDisabled]}
                  onPress={onStart}
                  disabled={disabled}
                >
                  <Play size={16} color="#FFFFFF" />
                  <Text style={styles.startButtonText}>
                    {stageName === 'outreach' ? 'Start Outreach' : 'Start Agent'}
                  </Text>
                </Pressable>
              )}

              {/* Confirmation Button */}
              {stageData.awaiting_confirmation && !stageData.human_intervention && (
                <Pressable
                  style={[styles.actionButton, styles.confirmButton, disabled && styles.actionButtonDisabled]}
                  onPress={onConfirm}
                  disabled={disabled}
                >
                  <CheckCircle2 size={16} color="#FFFFFF" />
                  <Text style={styles.confirmButtonText}>Confirm & Proceed</Text>
                </Pressable>
              )}

              {stageName === 'outreach' &&
               stageData.status === 'in_progress' &&
               !stageData.awaiting_confirmation &&
               !stageData.human_intervention &&
               onCompleteManually && (
                <Pressable
                  style={[styles.actionButton, styles.confirmButton, disabled && styles.actionButtonDisabled]}
                  onPress={onCompleteManually}
                  disabled={disabled}
                >
                  <CheckCircle2 size={16} color="#FFFFFF" />
                  <Text style={styles.confirmButtonText}>Mark Outreach Complete</Text>
                </Pressable>
              )}

              {/* Human Intervention / Resume AI */}
              {stageData.human_intervention ? (
                <Pressable
                  style={[styles.actionButton, styles.resumeButton, disabled && styles.actionButtonDisabled]}
                  onPress={onResumeAI}
                  disabled={disabled}
                >
                  <Play size={16} color="#2563EB" />
                  <Text style={styles.resumeButtonText}>Resume AI</Text>
                </Pressable>
              ) : stageData.status !== 'pending' && stageData.status !== 'completed' && (
                <Pressable
                  style={[styles.actionButton, styles.interveneButton, disabled && styles.actionButtonDisabled]}
                  onPress={() => setShowInterventionModal(true)}
                  disabled={disabled || stageData.status === 'completed'}
                >
                  <Pause size={16} color="#6B7280" />
                  <Text style={styles.interveneButtonText}>Intervene</Text>
                </Pressable>
              )}

              {/* Reset Button */}
              <Pressable
                style={[styles.actionButton, styles.resetButton, disabled && styles.actionButtonDisabled]}
                onPress={() => setShowResetConfirm(true)}
                disabled={disabled}
              >
                <RotateCcw size={16} color="#DC2626" />
                <Text style={styles.resetButtonText}>Reset</Text>
              </Pressable>
            </View>

            {/* Timestamps */}
            {(stageData.started_at || stageData.completed_at) && (
              <View style={styles.timestampsContainer}>
                {stageData.started_at && (
                  <Text style={styles.timestampText}>
                    Started: {new Date(stageData.started_at).toLocaleString()}
                  </Text>
                )}
                {stageData.completed_at && (
                  <Text style={styles.timestampText}>
                    Completed: {new Date(stageData.completed_at).toLocaleString()}
                  </Text>
                )}
              </View>
            )}
          </View>
        )}
      </View>

      {/* Intervention Modal */}
      <Modal
        visible={showInterventionModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowInterventionModal(false)}
      >
        <TouchableWithoutFeedback onPress={() => setShowInterventionModal(false)}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={styles.modalContent}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Human Intervention</Text>
                  <Pressable
                    onPress={() => setShowInterventionModal(false)}
                    style={styles.modalCloseButton}
                  >
                    <X size={20} color="#9CA3AF" />
                  </Pressable>
                </View>
                <View style={styles.modalBody}>
                  <Text style={styles.modalText}>
                    Taking over this stage will pause the AI agent. You can add notes and manually control progression.
                  </Text>
                  <TextInput
                    style={styles.modalInput}
                    placeholder="Add notes about your intervention (optional)"
                    value={interventionNotes}
                    onChangeText={setInterventionNotes}
                    multiline
                    numberOfLines={4}
                    textAlignVertical="top"
                  />
                </View>
                <View style={styles.modalActions}>
                  <Pressable
                    style={[styles.modalButton, styles.modalCancelButton]}
                    onPress={() => setShowInterventionModal(false)}
                  >
                    <Text style={styles.modalCancelButtonText}>Cancel</Text>
                  </Pressable>
                  <Pressable
                    style={[styles.modalButton, styles.modalConfirmButton]}
                    onPress={handleInterventionSubmit}
                  >
                    <Text style={styles.modalConfirmButtonText}>Take Over</Text>
                  </Pressable>
                </View>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* Reset Confirmation Modal */}
      <Modal
        visible={showResetConfirm}
        transparent
        animationType="fade"
        onRequestClose={() => setShowResetConfirm(false)}
      >
        <TouchableWithoutFeedback onPress={() => setShowResetConfirm(false)}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={styles.modalContent}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Reset Stage</Text>
                  <Pressable
                    onPress={() => setShowResetConfirm(false)}
                    style={styles.modalCloseButton}
                  >
                    <X size={20} color="#9CA3AF" />
                  </Pressable>
                </View>
                <View style={styles.modalBody}>
                  <Text style={styles.modalText}>
                    Are you sure you want to reset this stage? All progress will be cleared and the stage will return to pending status.
                  </Text>
                </View>
                <View style={styles.modalActions}>
                  <Pressable
                    style={[styles.modalButton, styles.modalCancelButton]}
                    onPress={() => setShowResetConfirm(false)}
                  >
                    <Text style={styles.modalCancelButtonText}>Cancel</Text>
                  </Pressable>
                  <Pressable
                    style={[styles.modalButton, styles.modalDeleteButton]}
                    onPress={handleReset}
                  >
                    <Text style={styles.modalDeleteButtonText}>Reset Stage</Text>
                  </Pressable>
                </View>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  stageCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    marginBottom: 12,
    ...shadows.sm,
  },
  stageHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
  },
  stageHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 12,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stageTitleContainer: {
    flex: 1,
  },
  stageTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  stageNumber: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6B7280',
    textTransform: 'uppercase',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  statusBadgeText: {
    fontSize: 11,
    fontWeight: '600',
  },
  stageTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 2,
  },
  stagePurposeCollapsed: {
    fontSize: 12,
    color: '#6B7280',
  },
  expandIcon: {
    marginLeft: 8,
  },
  stageBody: {
    paddingHorizontal: 16,
    paddingBottom: 16,
    gap: 16,
  },
  stagePurpose: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
  },
  interventionBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#FEF3C7',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#F59E0B',
  },
  interventionBadgeText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#D97706',
  },
  substepsContainer: {
    gap: 8,
  },
  substepsTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 4,
  },
  substepItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  substepIndicator: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#D1D5DB',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
  },
  substepIndicatorCompleted: {
    borderColor: '#16A34A',
    backgroundColor: '#F0FDF4',
  },
  substepIndicatorInProgress: {
    borderColor: '#2563EB',
    backgroundColor: '#EFF6FF',
  },
  substepText: {
    fontSize: 14,
    color: '#374151',
    flex: 1,
  },
  substepTextCompleted: {
    color: '#9CA3AF',
    textDecorationLine: 'line-through',
  },
  substepTextInProgress: {
    color: '#2563EB',
    fontWeight: '500',
  },
  notesContainer: {
    gap: 8,
  },
  notesHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 4,
  },
  notesTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#374151',
  },
  notesBox: {
    backgroundColor: '#F9FAFB',
    padding: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  notesText: {
    fontSize: 14,
    color: '#374151',
    lineHeight: 20,
  },
  actionsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 6,
    borderWidth: 1,
  },
  actionButtonDisabled: {
    opacity: 0.5,
  },
  startButton: {
    backgroundColor: '#16A34A',
    borderColor: '#16A34A',
  },
  startButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  confirmButton: {
    backgroundColor: '#2563EB',
    borderColor: '#2563EB',
  },
  confirmButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  interveneButton: {
    backgroundColor: '#FFFFFF',
    borderColor: '#D1D5DB',
  },
  interveneButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  resumeButton: {
    backgroundColor: '#EFF6FF',
    borderColor: '#2563EB',
  },
  resumeButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2563EB',
  },
  resetButton: {
    backgroundColor: '#FFFFFF',
    borderColor: '#FCA5A5',
  },
  resetButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#DC2626',
  },
  timestampsContainer: {
    gap: 4,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  timestampText: {
    fontSize: 12,
    color: '#9CA3AF',
  },
  clarifyFormContainer: {
    backgroundColor: '#F9FAFB',
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  honeyDonePlanContainer: {
    marginTop: 8,
  },
  providerSelectionContainer: {
    backgroundColor: '#F9FAFB',
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  overrideFormContainer: {
    marginTop: 8,
  },
  outreachProviderContainer: {
    marginTop: 8,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    width: '100%',
    maxWidth: 500,
    ...shadows.lg,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  modalCloseButton: {
    padding: 4,
  },
  modalBody: {
    padding: 24,
    gap: 16,
  },
  modalText: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
  },
  modalInput: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 6,
    padding: 12,
    fontSize: 14,
    minHeight: 100,
    textAlignVertical: 'top',
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    padding: 24,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  modalButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 6,
    minWidth: 100,
    alignItems: 'center',
  },
  modalCancelButton: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#D1D5DB',
  },
  modalCancelButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  modalConfirmButton: {
    backgroundColor: '#2563EB',
  },
  modalConfirmButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  modalDeleteButton: {
    backgroundColor: '#DC2626',
  },
  modalDeleteButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});
